<template>
    <div class="ProductDetailsFareAndActions">
        <ul
            v-if="hasTargetIcons"
            class="ProductDetailsFareAndActions-targets"
        >
            <li
                v-for="target in targetIcons"
                :key="target.id"
                class="d-flex align-center gap-2 text-body-2"
            >
                <icomoon-icon
                    :name="target.icon"
                    class="ProductDetailsFareAndActions-targetIcon"
                />
                {{ target.label }}
            </li>
        </ul>
        <price
            :display-weee-tax="true"
            :product="productDetails"
            :size="mdAndUp ? 'XLarge' : 'Large'"
            class="ProductDetailsFareAndActions-fare"
        />
        <div
            v-if="showAddToCartForWebApp"
            class="ProductDetailsFareAndActions-buttonContainer"
        >
            <v-btn
                class="ProductDetailsFareAndActions-button text-body-1 font-weight-bold"
                color="primary"
                elevation="0"
                size="large"
                @click="addCartAction"
            >
                {{ $t('button:add_to_cart') }}
            </v-btn>
        </div>
        <p
            v-if="showExpressPurchaseMessage"
            class="ProductDetailsFareAndActions-quickPurchase"
        >
            <v-icon class="ProductDetailsFareAndActions-quickPurchaseIcon">
                mdi-clock-fast
            </v-icon>
            <span
                v-html="$sanitize(quickPurchaseMessage)"
            />
        </p>
        <span
            v-if="productDetails.isCardReaderMandatory()"
            class="ProductDetailsFareAndActions-cardReaderMandatory"
        >
            <icomoon-icon
                :layersCount="3"
                name="CardReader"
                class="ProductDetailsFareAndActions-cardReaderMandatoryIcon"
            />
            <span
                v-html="$sanitize($t('product_details:card_reader_mandatory'))"
            />
        </span>
        <product-required-fare-profiles
            v-else-if="showRequiredFareProfilesModal"
            :required-fare-profiles="productDetails.requiredFareProfiles"
            :selected-fare-media="selectedFareMedia"
        />
    </div>
</template>

<script setup>
import IcomoonIcon from '@/StoreWeb/components/common/IcomoonIcon.vue'
import Price from '@/StoreWeb/components/common/Price'
import ProductRequiredFareProfiles from '@/StoreWeb/components/product/ProductRequiredFareProfiles'
import { useAddToCart } from '@/StoreWeb/js/composables/useAddToCart'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

const { mdAndUp } = useDisplay()
const { t } = useI18n()
const { addCartAction, productDetails, selectedFareMedia, showAddToCart, showAddToCartForWebApp } = useAddToCart()

const quickPurchaseMessage = t('quick_purchase:explanations').replace(/\\n/g, '<br>')

const targetIcons = computed(() => productDetails.value.getTargets())
const hasTargetIcons = computed(() => targetIcons.value.length > 0)
const showExpressPurchaseMessage = computed(() => {
    return showAddToCart.value && productDetails.value.expressPurchase
})
const showRequiredFareProfilesModal = computed(() => {
    return productDetails.value.requiredFareProfiles && productDetails.value.requiredFareProfiles.length > 0
})

</script>

<style scoped lang="scss">
@import 'globalScss';

.ProductDetailsFareAndActions {
    display: flex;
    flex-flow: column nowrap;
    gap: $s4;

    &-targets {
        display: flex;
        flex-direction: column;
        gap: $s1;
        width: 100%;
        margin-bottom: $s6;
        list-style: none;
    }

    &-targetIcon {
        font-size: $font-xlarge;
        color: $color-brandSecondary;
    }

    &-fare {
        order: 2;
        margin-bottom: $s6;

        &:last-child {
            margin-bottom: 0;
        }

        @media #{map-get($display-breakpoints, 'md-and-up')} {
            order: 1;
        }
    }

    &-buttonContainer {
        order: 3;
        width: 100%;
    }

    &-button {
        margin-bottom: $s8;
    }

    &-quickPurchase {
        display: flex;
        align-items: center;
        gap: $s4;
        margin-top: 0;
        margin-bottom: $s6;
        font-size: $font-xsmall;
        color: $color-brandSecondary;
    }

    &-quickPurchaseIcon.v-icon {
        font-size: $font-xlarge;
        color: inherit;
    }

    &-cardReaderMandatory {
        display: flex;
        gap: $s2;
        padding: $s4;
        border-radius: $border-radius-medium;
        background: $color-brandSecondaryLightBackground;
        font-weight: bold;
        color: $color-brandSecondary;
    }

    &-cardReaderMandatoryIcon {
        margin-top: 2px;
        font-size: $font-medium;
    }

    &-requiredFareProfiles {
        font-size: $font-xsmall;
        line-height: $font-large;
    }
}
</style>
